import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft, FaEnvelope, FaSave, FaUserTie } from "react-icons/fa";
import { toast } from "react-toastify";
import { createTenant, getTenantById, updateTenant } from "../../api/tenants";
import { useAppTheme } from "../../context/AppThemeContext";
import ClassNXLoader from "../ClassNXLoader";
import { invalidateCache } from "../../utils/requestCache";
import { getTenantPrincipals, inviteTenantPrincipal } from "../../api/principal";

const TENANTS_CACHE_KEY = "superadmin:tenants:viewAll";
const emptyForm = {
  adminName: "", schoolName: "", subdomain: "", adminEmail: "", adminMobile: "", country: "",
  plan: "FREE", campuses: "1", admins: "0", students: "0", status: "ACTIVE",
};

export default function SuperAdminTenantForm() {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useAppTheme();
  const isEditing = Boolean(tenantId);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [principals, setPrincipals] = useState([]);
  const [principalForm, setPrincipalForm] = useState({ name: "", email: "" });
  const [invitingPrincipal, setInvitingPrincipal] = useState(false);

  useEffect(() => {
    if (!tenantId) return;
    let active = true;
    getTenantById(tenantId).then((tenant) => {
      if (!active) return;
      if (!tenant) {
        toast.error("Unable to load tenant details.");
        navigate("/super-admin", { replace: true });
        return;
      }
      setForm({
        adminName: tenant.adminName || tenant.name || "",
        schoolName: tenant.schoolName || tenant.name || "",
        subdomain: tenant.subdomain || "",
        adminEmail: tenant.adminEmail || tenant.email || "",
        adminMobile: tenant.adminMobile || tenant.mobile || tenant.phone || "",
        country: tenant.country || "",
        plan: String(tenant.plan || tenant.subscriptionPlan || "FREE").toUpperCase(),
        campuses: String(tenant.campuses ?? 1),
        admins: String(tenant.admins ?? tenant.adminCount ?? 0),
        students: String(tenant.students ?? tenant.studentCount ?? 0),
        status: String(tenant.status || "ACTIVE").toUpperCase(),
      });
      setLoading(false);
    });
    return () => { active = false; };
  }, [navigate, tenantId]);

  useEffect(() => {
    if (!tenantId) return undefined;
    let active = true;
    getTenantPrincipals(tenantId).then((result) => {
      if (!active) return;
      const records = Array.isArray(result) ? result : result?.content || result?.items || result?.principals || [];
      setPrincipals(records);
    }).catch(() => { if (active) setPrincipals([]); });
    return () => { active = false; };
  }, [tenantId]);

  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const backToTenants = () => navigate("/super-admin?panel=tenantPortfolio");

  const submit = async (event) => {
    event.preventDefault();
    if (!form.adminName.trim() || !form.schoolName.trim()) {
      toast.error("Tenant name and school name are required.");
      return;
    }
    const payload = {
      ...form,
      adminName: form.adminName.trim(), schoolName: form.schoolName.trim(),
      subdomain: (form.subdomain || "").trim().toLowerCase(),
      adminEmail: form.adminEmail.trim(), adminMobile: form.adminMobile.trim(), country: form.country.trim(),
      campuses: Number(form.campuses || 0), admins: Number(form.admins || 0), students: Number(form.students || 0),
    };
    setSaving(true);
    try {
      if (isEditing) await updateTenant(tenantId, payload);
      else await createTenant(payload);
      invalidateCache(TENANTS_CACHE_KEY);
      toast.success(isEditing ? "Tenant updated." : "Tenant created successfully.");
      backToTenants();
    } catch (error) {
      toast.error(error?.message || "Unable to save tenant.");
    } finally { setSaving(false); }
  };

  const invitePrincipal = async (event) => {
    event.preventDefault();
    const name = principalForm.name.trim();
    const email = principalForm.email.trim();
    if (!name || !email) { toast.error("Principal name and email are required."); return; }
    setInvitingPrincipal(true);
    try {
      const created = await inviteTenantPrincipal(tenantId, { name, email });
      setPrincipals((current) => [...current, created || { name, email, active: false }]);
      setPrincipalForm({ name: "", email: "" });
      toast.success("Principal invitation sent. The account activates after password setup.");
    } catch (error) {
      toast.error(error?.message || "Unable to invite principal.");
    } finally { setInvitingPrincipal(false); }
  };

  const inputClass = `w-full rounded-xl border px-3 py-2 text-sm focus:border-fuchsia-500 focus:outline-none focus:ring-2 focus:ring-fuchsia-200 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300 bg-white text-slate-900"}`;
  if (loading) return <ClassNXLoader label="Loading tenant" detail="Preparing tenant details." />;

  return (
    <main className={`min-h-full p-4 sm:p-6 ${isDarkMode ? "bg-slate-950 text-slate-100" : "bg-slate-50 text-slate-900"}`}>
      <div className="mx-auto max-w-4xl">
        <button type="button" onClick={backToTenants} className="inline-flex items-center gap-2 text-sm font-semibold text-fuchsia-600 hover:text-fuchsia-700"><FaArrowLeft /> Back to tenant list</button>
        <section className={`mt-5 rounded-3xl border p-6 shadow-lg sm:p-8 ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Tenants</p>
          <h1 className="mt-2 text-2xl font-bold">{isEditing ? "Edit tenant" : "Add tenant"}</h1>
          <form onSubmit={submit} className="mt-6 grid gap-4 sm:grid-cols-2">
            {[
              ["adminName", "Name", "text", true], ["schoolName", "School name", "text", true],
              ["subdomain", "Subdomain", "text", false, "e.g. fatowef (fatowef.classnx.com)"],
              ["adminEmail", "Admin email", "email"], ["adminMobile", "Admin mobile", "tel"],
              ["country", "Country", "text"], ["campuses", "Campuses", "number"],
              ["admins", "Admins", "number"], ["students", "Students", "number"],
            ].map(([key, label, type, required, placeholder]) => (
              <label key={key} className="text-sm">
                <span className="mb-1 block font-medium">{label}</span>
                <input
                  type={type}
                  min={type === "number" ? "0" : undefined}
                  required={required}
                  placeholder={placeholder}
                  value={form[key]}
                  onChange={(e) => {
                    const val = key === "subdomain"
                      ? e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "")
                      : e.target.value;
                    update(key, val);
                  }}
                  className={inputClass}
                />
              </label>
            ))}
            <label className="text-sm"><span className="mb-1 block font-medium">Plan</span><select value={form.plan} onChange={(e) => update("plan", e.target.value)} className={inputClass}><option value="FREE">FREE</option><option value="PREMIUM">PREMIUM</option></select></label>
            <label className="text-sm"><span className="mb-1 block font-medium">Status</span><select value={form.status} onChange={(e) => update("status", e.target.value)} className={inputClass}><option value="ACTIVE">ACTIVE</option><option value="TRIAL">TRIAL</option><option value="SUSPENDED">SUSPENDED</option></select></label>
            <div className="flex flex-wrap gap-3 sm:col-span-2">
              <button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-fuchsia-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-fuchsia-700 disabled:opacity-60"><FaSave /> {saving ? "Saving..." : isEditing ? "Save changes" : "Add tenant"}</button>
              <button type="button" onClick={backToTenants} className={`rounded-full border px-5 py-2.5 text-sm font-semibold ${isDarkMode ? "border-slate-700" : "border-slate-300"}`}>Cancel</button>
            </div>
          </form>
        </section>
        {isEditing && (
          <section className={`mt-6 border p-6 shadow-lg sm:p-8 ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
            <div className="flex items-start gap-3"><span className="grid h-11 w-11 shrink-0 place-items-center bg-blue-50 text-blue-700"><FaUserTie /></span><div><p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-600">School leadership</p><h2 className="mt-1 text-xl font-bold">Principal account</h2><p className="mt-1 text-sm text-slate-500">One principal is allowed per school. The invitation uses the secure password-setup flow.</p></div></div>
            {principals.length > 0 ? <div className={`mt-6 border p-4 ${isDarkMode ? "border-slate-700 bg-slate-950" : "border-slate-200 bg-slate-50"}`}>{principals.map((principal, index) => <div key={principal.id || principal.email || index} className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center"><div><p className="font-bold">{principal.name || principal.fullName || "Principal"}</p><p className="mt-1 text-sm text-slate-500">{principal.email}</p></div><span className={`w-fit px-3 py-1 text-xs font-bold uppercase tracking-wider ${principal.active || String(principal.status).toUpperCase() === "ACTIVE" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{principal.active || String(principal.status).toUpperCase() === "ACTIVE" ? "Active" : "Invitation pending"}</span></div>)}</div> : <form onSubmit={invitePrincipal} className="mt-6 grid gap-4 sm:grid-cols-2"><label className="text-sm"><span className="mb-1 block font-medium">Principal name</span><input value={principalForm.name} onChange={(event) => setPrincipalForm((current) => ({ ...current, name: event.target.value }))} className={inputClass} required /></label><label className="text-sm"><span className="mb-1 block font-medium">Principal email</span><input type="email" value={principalForm.email} onChange={(event) => setPrincipalForm((current) => ({ ...current, email: event.target.value }))} className={inputClass} required /></label><div className="sm:col-span-2"><button type="submit" disabled={invitingPrincipal} className="inline-flex items-center gap-2 bg-blue-700 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-800 disabled:opacity-60"><FaEnvelope />{invitingPrincipal ? "Sending invitation..." : "Invite principal"}</button></div></form>}
          </section>
        )}
      </div>
    </main>
  );
}
